import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import {
  Smartphone, KeyRound, Network, Coins, Cpu, Link2, Zap,
  Pickaxe, ArrowDownToLine, Lock, Unlock, Repeat, ArrowLeftRight, Waypoints,
  Building2, Banknote, Target, ChevronRight, ChevronLeft, X, Clock, DollarSign, Shield,
  ArrowDown, Sparkles, GitBranch, PlayCircle, FileDown, Loader2, ExternalLink, Copy, Presentation,
  Check, Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// =====================================================================
//  STAGE / NODE DATA
//  Each node is fully describable: short subtitle (in card) + drawer detail.
// =====================================================================
const STAGES = [
  {
    id: "setup",
    label: "Stage 01",
    title: "Wallet setup · Section A",
    subtitle: "A1 – A4 · one-time · ~15 minutes",
    accent: "#4060FF",
    accentSoft: "rgba(64,96,255,0.12)",
    flowLabel: "USDT capital deploying in",
    flowAsset: "USDT",
    nodes: [
      {
        id: "a1", slot: "a1", refCode: "A1",
        icon: <Smartphone size={20} />, title: "Install TokenPocket",
        sub: "App Store · Play Store · Chrome ext",
        chips: ["~2 min", "Free"],
        detail: "Download TokenPocket from the official source. On mobile, confirm the developer is TP Global Ltd. On desktop, install the Chrome / Firefox / Edge extension from the official Web Store. This is where every future wallet action happens.",
        inputs: [], outputs: ["TokenPocket ready to open"],
        fees: "None.", time: "~2 minutes.",
        risks: "Fake clones exist — verify the developer name before installing.",
      },
      {
        id: "a2", slot: "a2", refCode: "A2",
        icon: <KeyRound size={20} />, title: "Create wallet & save 12-word phrase",
        sub: "Password + recovery phrase on paper",
        chips: ["~5 min", "Critical"],
        detail: "Inside TokenPocket, create a fresh wallet, set a password, then write the 12-word recovery phrase on paper. This phrase is the only way to restore the wallet if your device is lost — CaryPact, exchanges, and BotChainCP support cannot recover it for you.",
        inputs: [], outputs: ["Wallet address", "Seed phrase (paper)"],
        fees: "None.", time: "~5 minutes.",
        risks: "If you screenshot, cloud-sync, or share the 12 words, anyone with them can drain you. Pen + paper only.",
      },
      {
        id: "a3", slot: "a3", refCode: "A3",
        icon: <Link2 size={20} />, title: "Open your sponsor's CaryPact invite",
        sub: "Register under your leader's team",
        chips: ["~3 min", "Required"],
        detail: "Open the CaryPact invite link your sponsor gave you (it looks like https://app.carypact.com/?inviteCode=XXXX). Register there so you're placed under the correct team tree. Sign the proof-of-ownership message (no gas). Without this, team-rank rewards can't be attributed to your upline.",
        inputs: ["Sponsor invite link"], outputs: ["CaryPact account linked to sponsor"],
        fees: "None.", time: "~3 minutes.",
        risks: "The invite code cannot be added later. Register with the right link the first time.",
      },
      {
        id: "a4", slot: "a4", refCode: "A4",
        icon: <Coins size={20} />, title: "Import USDT + CA tokens",
        sub: "Custom token contracts on BOT Chain",
        chips: ["~2 min", "Free"],
        detail: "BOT Chain doesn't auto-display USDT or CA — add them once as custom tokens (USDT 6 decimals, CA 18 decimals). Without this step, your balances will look like zero even when they're not. TokenPocket has \"Add Token\" in the token list — paste the addresses your sponsor provides.",
        inputs: ["Contract addresses"], outputs: ["USDT + CA visible in TokenPocket"],
        fees: "None.", time: "~2 minutes.",
        risks: "Scammers create fake 'USDT' / 'CA' tokens. Use the exact addresses your sponsor publishes.",
      },
    ],
  },
  {
    id: "transactions",
    label: "Stage 02",
    title: "First transactions · Section B",
    subtitle: "B1 – B4 · one-time · ~20 minutes",
    accent: "#6B4FFF",
    accentSoft: "rgba(107,79,255,0.12)",
    flowLabel: "USDT converting to hashrate + CA",
    flowAsset: "USDT",
    nodes: [
      {
        id: "b1", slot: "b1", refCode: "B1",
        icon: <DollarSign size={20} />, title: "Fund your wallet",
        sub: "Send USDT (BSC) into TokenPocket",
        chips: ["~10 min", "From exchange"],
        detail: "Buy USDT on a regulated exchange (Coinstore, Binance, Coinbase, etc.) and withdraw it to your TokenPocket wallet on the BSC (BEP20) network. Also send a small amount of BNB (~$30) — it's your gas budget for every future transaction.",
        inputs: ["USD via bank"], outputs: ["USDT (BNB Chain)", "BNB gas"],
        fees: "Exchange fees + withdrawal network fee.", time: "~10 minutes once KYC done.",
        risks: "Wrong-network withdrawal = funds lost forever. Always select BSC (BEP20).",
      },
      {
        id: "b2", slot: "b2", refCode: "B2",
        icon: <Cpu size={20} />, title: "Buy hashrate",
        sub: "USDT → computing power (T/H)",
        chips: ["~2 min", "Gas: ~$0.10"],
        detail: "Spend USDT on the CaryPact contract — it issues you hashrate (T/H) which begins producing CA tokens daily. Start small at $100 to test the round-trip (buy → claim → swap → cash out) before scaling capital.",
        inputs: ["USDT (BOT Chain)"], outputs: ["Hashrate position"],
        fees: "~$0.10 in BOT for gas.", time: "~2 minutes.",
        risks: "Yield dilutes over time (compensation rate). Treat your first $100 as a paid dress rehearsal.",
      },
      {
        id: "b3", slot: "b3", refCode: "B3",
        icon: <Lock size={20} />, title: "Stake your CA",
        sub: "Fixed or flexible · earn daily yield",
        chips: ["30/90/180/360d", "Highest yield"],
        detail: "Send your CA into CaryPact's staking pool. Fixed-term stakes (30/90/180/360d) pay up to ~0.75%/day; flexible stakes let you withdraw anytime at ~0.30%/day. Rewards accrue daily and can be claimed or restaked to compound.",
        inputs: ["CA (min 5)"], outputs: ["Daily CA yield"],
        fees: "Gas only.", time: "Term lock (or flexible).",
        risks: "Fixed stakes are illiquid for the term. Match the lock-up to when you actually need the capital back.",
      },
      {
        id: "b4", slot: "b4", refCode: "B4",
        icon: <Sparkles size={20} />, title: "Copy your CaryPact invite link",
        sub: "Share with your community",
        chips: ["~1 min", "Instant"],
        detail: "Once your CaryPact account is active, copy your personal invite link from the app (User Center → Invite Friends). This is the link you share with everyone you bring in — their sign-ups get placed under you in the team tree, which drives your community rewards.",
        inputs: [], outputs: ["Your personal CaryPact invite link"],
        fees: "None.", time: "~1 minute.",
        risks: "Sharing anyone else's link (not yours) means their sign-ups are attributed to someone else.",
      },
    ],
  },
  {
    id: "earning",
    label: "Stage 03",
    title: "Daily earning cycle",
    subtitle: "Automatic · CA + team rewards flow in",
    accent: "#00B344",
    accentSoft: "rgba(0,179,68,0.13)",
    flowLabel: "CA tokens accruing daily",
    flowAsset: "CA",
    nodes: [
      {
        id: "mining", refCode: null,
        icon: <Pickaxe size={20} />, title: "Mining rewards",
        sub: "Hashrate × compensation rate → CA",
        chips: ["Continuous", "On-chain"],
        detail: "Your hashrate position produces CA tokens daily inside the CaryPact contract. Daily payout = hashrate × current compensation rate (which trends down as more participants join). BotChainCP tracks the actual realized rate for you on the Dashboard → Mining view.",
        inputs: ["Hashrate (T/H)"], outputs: ["CA accruing inside contract"],
        fees: "None.", time: "Every block (~3 sec).",
        risks: "Compensation rate dilution — yield drops over time. Plan exits accordingly.",
      },
      {
        id: "staking", refCode: null,
        icon: <Zap size={20} />, title: "Staking rewards",
        sub: "Daily CA yield from B3 stakes",
        chips: ["Auto-compounds", "Claim anytime"],
        detail: "Every stake placed in B3 pays out CA rewards daily. Rewards can auto-compound back into principal or be claimed to your wallet. The Dashboard → Staking view shows every open position, term left, and realized rewards.",
        inputs: ["Active stakes"], outputs: ["Daily CA rewards"],
        fees: "Gas only when claiming.", time: "Continuous.",
        risks: "Fixed-term principal drips back proportionally (not a lump sum at end of term).",
      },
      {
        id: "team", refCode: null,
        icon: <Network size={20} />, title: "Team rewards",
        sub: "% of what your downline earns",
        chips: ["Automatic", "Rank-based"],
        detail: "Every recruit who joined via your B4 invite link contributes team rewards to you based on your rank in the CaryPact tree. BotChainCP's Dashboard → Team view aggregates this per portfolio so you see exactly how much your community produced.",
        inputs: ["Downline mining + staking activity"], outputs: ["Team CA + USDT rewards"],
        fees: "None.", time: "Continuous.",
        risks: "Team rewards depend on your rank staying active — inactive periods can pause payouts.",
      },
      {
        id: "cashout", refCode: null,
        icon: <ArrowDownToLine size={20} />, title: "Cash out (when ready)",
        sub: "CA → BOT → USDT → bank",
        chips: ["~15 min", "Multi-hop"],
        detail: "When you want liquidity: swap CA → BOT inside CaryPact, swap BOT → USDT on BDEX, bridge USDT to BNB Chain, deposit at your exchange, sell to USD, withdraw to bank. Log withdrawals on BotChainCP so they count toward your capital-recovery KPI.",
        inputs: ["CA rewards"], outputs: ["USD in your bank"],
        fees: "Gas + DEX + bridge + exchange fees (~1%).", time: "~15 minutes on-chain, 1–3 days bank.",
        risks: "Wrong-network selection at any hop = funds lost. Verify From/To before every signature.",
      },
    ],
  },
];

// =====================================================================
//  RE-USED PIECES
// =====================================================================
function FlowArrow({ accent }) {
  return (
    <div className="hidden md:flex items-center justify-center flex-shrink-0" style={{ width: 36 }}>
      <svg width="36" height="14" viewBox="0 0 36 14" fill="none">
        <line x1="0" y1="7" x2="30" y2="7" stroke={accent} strokeWidth="2" strokeDasharray="3 4">
          <animate attributeName="stroke-dashoffset" from="0" to="-14" dur="0.9s" repeatCount="indefinite" />
        </line>
        <path d="M28 2 L36 7 L28 12" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

function MobileArrow({ accent }) {
  return (
    <div className="flex md:hidden items-center justify-center w-full" style={{ height: 24 }}>
      <svg width="14" height="24" viewBox="0 0 14 24" fill="none">
        <line x1="7" y1="0" x2="7" y2="18" stroke={accent} strokeWidth="2" strokeDasharray="3 4">
          <animate attributeName="stroke-dashoffset" from="0" to="-14" dur="0.9s" repeatCount="indefinite" />
        </line>
        <path d="M2 16 L7 24 L12 16" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

function NodeCard({ node, accent, accentSoft, onClick, index, hasVideo }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => onClick(node)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="glass text-left transition-all fade-up relative group flex-1 min-w-0"
      style={{
        animationDelay: `${index * 60}ms`,
        padding: "16px 14px",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hovered ? "0 18px 40px rgba(15,18,38,0.10)" : "0 4px 14px rgba(15,18,38,0.04)",
        cursor: "pointer",
        minWidth: 0,
        maxWidth: 200,
      }}
      data-testid={`flow-node-${node.id}`}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white mb-3" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}AA)`, boxShadow: `0 6px 14px ${accent}44` }}>
        {node.icon}
      </div>
      <h3 className="font-display text-sm font-semibold tracking-tight text-ink leading-tight">{node.title}</h3>
      <p className="text-[11px] text-ink-2 mt-1 leading-snug">{node.sub}</p>
      <div className="flex flex-wrap gap-1 mt-2.5">
        {node.chips.map((c) => (
          <span key={c} className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: accentSoft, color: accent }}>{c}</span>
        ))}
      </div>
      {(hasVideo || node.slot) && (
        <div className="flex items-center gap-1.5 mt-2.5 text-[9px] uppercase tracking-wider font-mono text-ink-3" data-testid={`flow-node-media-${node.id}`}>
          {hasVideo && <span className="inline-flex items-center gap-0.5"><PlayCircle size={10} style={{ color: accent }} /> Video</span>}
          {hasVideo && node.slot && <span>·</span>}
          {node.slot && <span className="inline-flex items-center gap-0.5"><FileDown size={10} style={{ color: accent }} /> Slides</span>}
        </div>
      )}
    </button>
  );
}

function DiamondNode({ node, accent, accentSoft, onClick, index }) {
  return (
    <button
      onClick={() => onClick(node)}
      className="text-left transition-all fade-up flex-shrink-0 relative"
      style={{ animationDelay: `${index * 60}ms`, width: 168, height: 168 }}
      data-testid={`flow-node-${node.id}`}
    >
      <div
        className="absolute inset-0 transition-transform hover:scale-105 hover:-translate-y-1"
        style={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px)",
          transform: "rotate(45deg)",
          border: `1.5px solid ${accent}44`,
          boxShadow: "0 14px 32px rgba(15,18,38,0.08)",
          borderRadius: 18,
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white mb-1.5" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}AA)` }}>
          {node.icon}
        </div>
        <h3 className="font-display text-xs font-semibold tracking-tight leading-tight">{node.title}</h3>
        <p className="text-[10px] text-ink-2 mt-0.5 leading-tight">{node.sub}</p>
        <span className="text-[9px] font-semibold uppercase tracking-wider mt-1.5 px-1.5 py-0.5 rounded" style={{ background: accentSoft, color: accent }}>Decision</span>
      </div>
    </button>
  );
}

// =====================================================================
//  VIDEO + DOCS EMBEDS (used inside the drawer for A1..A4, B1..B2)
// =====================================================================
function SelfHostedVideo({ src }) {
  if (!src) return null;
  return (
    <div className="rounded-xl overflow-hidden border border-black/5 bg-black flex items-center justify-center" style={{ boxShadow: "0 12px 32px rgba(15,18,38,0.14)", maxHeight: "42vh" }} data-testid="flow-drawer-video">
      <video
        controls
        playsInline
        preload="metadata"
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        className="w-auto max-w-full object-contain"
        style={{ maxHeight: "42vh" }}
        src={src}
      />
    </div>
  );
}

// Inline PowerPoint viewer — mirrors the new onboarding: converts the deck to
// slides on the server and shows them inline (tap / prev / next), with a
// download fallback. Replaces the old download-only list.
function StepDocs({ slot, accent }) {
  const [docs, setDocs] = useState([]);
  const [manifests, setManifests] = useState({});
  const [idx, setIdx] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const token = localStorage.getItem("cp_token") || "";

  useEffect(() => {
    if (!slot) { setDocs([]); return; }
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/videos/docs/for-slot/${slot}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!alive) return;
        const list = r.data?.docs || [];
        const results = await Promise.all(list.map((d) =>
          axios.get(`${API}/videos/docs/${d.id}/slides`, { headers: { Authorization: `Bearer ${token}` } })
            .then((rr) => [d.id, rr.data])
            .catch(() => [d.id, { count: 0, renderable: false }])
        ));
        if (!alive) return;
        setDocs(list);
        setManifests(Object.fromEntries(results));
        setIdx(Object.fromEntries(results.map(([id, m]) => [id, m?.count ? 1 : 0])));
        const firstR = list.find((d) => (results.find(([id]) => id === d.id)?.[1])?.renderable);
        setActiveId(firstR?.id || list[0]?.id || null);
      })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot]);

  const kb = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  const slideUrl = (docId, n) => `${API}/videos/docs/${docId}/slide/${n}?auth=${encodeURIComponent(token)}`;

  const downloadDoc = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const r = await axios.get(`${API}/videos/docs/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const stem = (doc.display_name || (doc.filename || "document").replace(/\.[^.]+$/, ""));
      a.download = `${stem}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silent — user can retry
    } finally { setDownloadingId(null); }
  };

  const stepSlide = (docId, dir) => {
    const m = manifests[docId];
    if (!m?.count) return;
    setIdx((prev) => {
      const cur = prev[docId] || 1;
      return { ...prev, [docId]: Math.max(1, Math.min(m.count, cur + dir)) };
    });
  };

  if (loading) return (
    <div className="rounded-xl border border-black/5 p-6 flex items-center justify-center" style={{ background: "rgba(15,18,38,0.02)" }} data-testid="flow-drawer-docs-loading">
      <Loader2 size={16} className="animate-spin text-ink-3" />
    </div>
  );
  if (!docs.length) return null;

  const renderable = docs.filter((d) => manifests[d.id]?.renderable && manifests[d.id]?.count > 0);
  const active = renderable.find((d) => d.id === activeId) || renderable[0] || null;

  return (
    <div className="space-y-3" data-testid="flow-drawer-docs">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          <Presentation size={12} />
        </div>
        <h5 className="text-xs font-bold uppercase tracking-wider text-ink-2">Slides for this step</h5>
      </div>

      {active ? (() => {
        const m = manifests[active.id];
        const cur = idx[active.id] || 1;
        const atFirst = cur <= 1, atLast = cur >= m.count;
        return (
          <div className="rounded-xl overflow-hidden border border-black/5 bg-black" data-testid={`flow-slides-${active.id}`} style={{ boxShadow: "0 12px 32px rgba(15,18,38,0.14)" }}>
            {renderable.length > 1 && (
              <div className="flex items-center gap-1 px-2 py-1.5 overflow-x-auto" style={{ background: "rgba(255,255,255,0.05)" }}>
                {renderable.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setActiveId(d.id)}
                    className="text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap transition-colors"
                    style={d.id === active.id ? { background: `${accent}44`, border: `1px solid ${accent}`, color: "#fff" } : { border: "1px solid transparent", color: "#94a3b8" }}
                    data-testid={`flow-slides-tab-${d.id}`}
                  >
                    {d.display_name}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => stepSlide(active.id, 1)}
              disabled={atLast}
              className={`w-full bg-black flex items-center justify-center relative group ${atLast ? "cursor-default" : "cursor-pointer"}`}
              style={{ height: "40vh" }}
              data-testid={`flow-slides-advance-${active.id}`}
              aria-label={atLast ? "Last slide" : `Advance to slide ${cur + 1}`}
            >
              <img
                key={`${active.id}-${cur}`}
                src={slideUrl(active.id, cur)}
                alt={`Slide ${cur} of ${m.count} — ${active.display_name}`}
                className="max-w-full max-h-full w-auto h-auto object-contain"
                loading="eager"
                data-testid={`flow-slides-img-${active.id}`}
                draggable={false}
              />
              {!atLast && (
                <span className="pointer-events-none absolute bottom-3 right-3 px-2 py-1 rounded-md bg-black/60 border border-white/10 text-[10px] font-mono text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">
                  Tap slide to advance
                </span>
              )}
            </button>
            <div className="flex items-center justify-between gap-2 px-3 py-2" style={{ background: "rgba(255,255,255,0.05)" }}>
              <button
                onClick={() => stepSlide(active.id, -1)}
                disabled={atFirst}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-slate-300 hover:text-white disabled:opacity-40 disabled:cursor-default"
                data-testid={`flow-slides-prev-${active.id}`}
              >
                <ChevronLeft size={13} /> Prev
              </button>
              <div className="text-[11px] font-mono text-slate-300" data-testid={`flow-slides-counter-${active.id}`}>
                Slide <span className="text-white font-semibold">{cur}</span> / {m.count}
              </div>
              <button
                onClick={() => stepSlide(active.id, 1)}
                disabled={atLast}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-slate-300 hover:text-white disabled:opacity-40 disabled:cursor-default"
                data-testid={`flow-slides-next-${active.id}`}
              >
                Next <ChevronRight size={13} />
              </button>
            </div>
          </div>
        );
      })() : null}

      <ul className="space-y-2">
        {docs.map((d) => (
          <li key={d.id} className="rounded-lg border border-black/5 bg-white/70 p-2.5 flex items-center gap-3" data-testid={`flow-doc-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-ink truncate">{d.display_name}</div>
              <div className="text-[10px] text-ink-3 uppercase tracking-wide font-mono">
                {(d.ext || "PPTX").toUpperCase()} · {kb(d.size || 0)}
                {manifests[d.id]?.count ? ` · ${manifests[d.id].count} slides` : ""}
              </div>
            </div>
            <button
              onClick={() => downloadDoc(d)}
              disabled={downloadingId === d.id}
              className="btn-secondary flex-shrink-0"
              style={{ padding: "6px 10px", fontSize: 11 }}
              data-testid={`flow-doc-dl-${d.id}`}
            >
              {downloadingId === d.id ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
              Download
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Small consistent section header used across the modal.
function SectionLabel({ icon, accent, children, testId }) {
  return (
    <div className="flex items-center gap-2 mb-3" data-testid={testId}>
      <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}1F`, color: accent }}>{icon}</div>
      <h5 className="text-[11px] font-bold uppercase tracking-wider text-ink-2">{children}</h5>
    </div>
  );
}

// The exact copy-items the onboarding shows for this step (contract addresses,
// symbols, decimals, invite links, …). Same data source as OnboardingV2.
function CopyItems({ items, accent }) {
  const [copied, setCopied] = useState(null);
  const copy = async (v, label) => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); }
    catch { toast.error("Couldn't copy — long-press / right-click to copy manually"); }
    setCopied(v); setTimeout(() => setCopied((c) => (c === v ? null : c)), 1400);
  };
  if (!items?.length) return null;
  return (
    <div data-testid="flow-copy-items">
      <SectionLabel icon={<Copy size={12} />} accent={accent}>Copy these details</SectionLabel>
      <div className="space-y-1.5">
        {items.map((it, i) => {
          const isUrl = /^https?:\/\//i.test(it.value || "");
          const done = copied === it.value;
          return (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-black/[0.06] bg-white px-3 py-2.5" data-testid={`flow-copy-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-ink-3">{it.label}</div>
                {isUrl ? (
                  <a href={it.value} target="_blank" rel="noreferrer" className="block text-xs font-mono truncate hover:underline" style={{ color: accent }} title={it.value} data-testid={`flow-copy-link-${i}`}>{it.value}</a>
                ) : (
                  <div className="text-sm font-mono text-ink truncate" title={it.value}>{it.value}</div>
                )}
              </div>
              <button
                onClick={() => copy(it.value, it.label)}
                className="inline-flex items-center gap-1 flex-shrink-0 rounded-lg text-[11px] font-semibold transition-colors"
                style={{ padding: "6px 11px", background: done ? "rgba(0,179,68,0.12)" : `${accent}14`, color: done ? "#059669" : accent, border: `1px solid ${done ? "rgba(0,179,68,0.35)" : `${accent}33`}` }}
                data-testid={`flow-copy-btn-${i}`}
              >
                {done ? <Check size={12} /> : <Copy size={12} />} {done ? "Copied" : "Copy"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// "Good to know" callout for the onboarding step's notes.
function StepNotes({ notes }) {
  if (!notes?.length) return null;
  return (
    <div className="rounded-xl p-4" style={{ background: "rgba(217,119,6,0.07)", border: "1px solid rgba(217,119,6,0.20)" }} data-testid="flow-notes">
      <div className="flex items-center gap-2 mb-2">
        <Info size={13} style={{ color: "#B45309" }} />
        <h5 className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#B45309" }}>Good to know</h5>
      </div>
      <ul className="space-y-1.5">
        {notes.map((n, i) => (
          <li key={i} className="text-xs text-ink-2 leading-relaxed flex gap-2"><span style={{ color: "#B45309" }}>·</span><span>{n}</span></li>
        ))}
      </ul>
    </div>
  );
}

// Links the step needs (e.g. the CaryPact invite link) — shown inside the modal.
function StepLinks({ links, accent }) {
  const [copied, setCopied] = useState(null);
  const copy = (v) => {
    try { navigator.clipboard?.writeText(v); toast.success("Link copied"); } catch { /* noop */ }
    setCopied(v); setTimeout(() => setCopied((c) => (c === v ? null : c)), 1500);
  };
  if (!links.length) return null;
  return (
    <div data-testid="flow-drawer-links">
      <SectionLabel icon={<Link2 size={12} />} accent={accent}>Links you&rsquo;ll need</SectionLabel>
      <ul className="space-y-1.5">
        {links.map((l, i) => (
          <li key={`${l.href}-${i}`} className="rounded-xl border border-black/[0.06] bg-white px-3 py-2.5" data-testid={`flow-link-${i}`}>
            <div className="text-xs font-semibold text-ink mb-1">{l.label}</div>
            <div className="flex items-center gap-3">
              <a href={l.href} target="_blank" rel="noreferrer" className="text-[11px] font-mono truncate flex-1 hover:underline" style={{ color: accent }}>{l.href}</a>
              {l.copy ? (
                <button onClick={() => copy(l.href)} className="inline-flex items-center gap-1 flex-shrink-0 rounded-lg text-[11px] font-semibold" style={{ padding: "6px 11px", background: `${accent}14`, color: accent, border: `1px solid ${accent}33` }} data-testid={`flow-link-copy-${i}`}>
                  {copied === l.href ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                </button>
              ) : (
                <a href={l.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 flex-shrink-0 rounded-lg text-[11px] font-semibold" style={{ padding: "6px 11px", background: `${accent}14`, color: accent, border: `1px solid ${accent}33` }} data-testid={`flow-link-open-${i}`}>
                  <ExternalLink size={11} /> Open
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =====================================================================
//  MODAL  (full-screen — video + inline slides + all step info)
// =====================================================================
function NodeDrawer({ node, accent, stageName, slotToUrl, content, invite, onClose }) {
  useEffect(() => {
    if (!node) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [node, onClose]);

  if (!node) return null;
  const rawUrl = node.slot ? slotToUrl?.[node.slot] : null;
  const videoSrc = rawUrl ? `${process.env.REACT_APP_BACKEND_URL}${rawUrl}?auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}` : null;

  const copyItems = content?.copy_items || [];
  const notes = content?.notes || [];

  const links = [];
  if (node.slot === "a3" && !copyItems.length) links.push({ label: "Open CaryPact to register under your sponsor", href: "https://app.carypact.com" });
  if (node.slot === "b4" && invite?.carypact_invite_link) links.push({ label: "Your personal CaryPact invite link", href: invite.carypact_invite_link, copy: true });

  const hasMeta = node.inputs?.length > 0 || node.outputs?.length > 0;
  const Divider = () => <div className="h-px bg-black/[0.06]" />;

  return createPortal(
    <div onClick={onClose} className="fixed inset-0 z-[100] flex" style={{ background: "rgba(8,10,28,0.55)", backdropFilter: "blur(6px)" }} data-testid="flow-drawer">
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-screen h-screen flex flex-col overflow-hidden fade-up"
        style={{ background: "radial-gradient(1200px 600px at 15% -10%, rgba(64,96,255,0.05), transparent), linear-gradient(180deg, #F7F9FF, #FFFFFF)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 md:px-8 py-4 flex-shrink-0" style={{ borderBottom: "1px solid rgba(15,18,38,0.06)", background: "rgba(247,249,255,0.85)", backdropFilter: "blur(14px)" }}>
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white flex-shrink-0" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}AA)`, boxShadow: `0 8px 18px ${accent}44` }}>
              {node.icon}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 truncate">{[stageName, node.refCode ? `Step ${node.refCode}` : null].filter(Boolean).join(" · ")}</div>
              <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight truncate">{node.title}</h2>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white shadow-sm hover:shadow-md border border-black/[0.06] flex items-center justify-center text-ink-2 hover:text-ink flex-shrink-0 transition-shadow" data-testid="flow-drawer-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-[1360px] mx-auto w-full px-5 md:px-8 py-6">
            <div className="grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-6 lg:gap-8 items-start">
              {/* Media */}
              <div className="space-y-5 min-w-0">
                {videoSrc && (
                  <div className="space-y-2">
                    <SectionLabel icon={<PlayCircle size={12} />} accent={accent}>Watch this step</SectionLabel>
                    <SelfHostedVideo src={videoSrc} />
                  </div>
                )}
                {node.slot && <StepDocs slot={node.slot} accent={accent} />}
                {!videoSrc && !node.slot && (
                  <div className="rounded-2xl border border-black/[0.06] p-10 text-center text-sm text-ink-3 bg-white/60" data-testid="flow-drawer-no-media">
                    This step doesn&rsquo;t have a video or slides — everything you need is in the details panel.
                  </div>
                )}
              </div>

              {/* Details panel */}
              <div className="rounded-2xl border border-black/[0.06] bg-white/70 p-5 md:p-6 space-y-6" data-testid="flow-drawer-details">
                <div>
                  <SectionLabel icon={<Info size={12} />} accent={accent}>What happens here</SectionLabel>
                  <p className="text-sm text-ink-2 leading-relaxed">{node.detail}</p>
                </div>

                {copyItems.length > 0 && (<><Divider /><CopyItems items={copyItems} accent={accent} /></>)}
                {links.length > 0 && (<><Divider /><StepLinks links={links} accent={accent} /></>)}
                {notes.length > 0 && (<><Divider /><StepNotes notes={notes} /></>)}

                {hasMeta && (
                  <>
                    <Divider />
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl border border-black/[0.06] bg-white p-3.5">
                        <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-1.5">Inputs</div>
                        {(!node.inputs || node.inputs.length === 0) ? <div className="text-xs text-ink-3 italic">—</div> : node.inputs.map((x) => <div key={x} className="text-xs text-ink mt-0.5">• {x}</div>)}
                      </div>
                      <div className="rounded-xl border border-black/[0.06] bg-white p-3.5">
                        <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-1.5">Outputs</div>
                        {(!node.outputs || node.outputs.length === 0) ? <div className="text-xs text-ink-3 italic">—</div> : node.outputs.map((x) => <div key={x} className="text-xs text-ink mt-0.5">• {x}</div>)}
                      </div>
                    </div>
                  </>
                )}

                <Divider />
                <div className="space-y-2.5">
                  <DetailRow icon={<DollarSign size={13} />} label="Fees" value={node.fees} />
                  <DetailRow icon={<Clock size={13} />} label="Time" value={node.time} />
                  <DetailRow icon={<Shield size={13} />} label="Risk" value={node.risks} red />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DetailRow({ icon, label, value, red = false }) {
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: red ? "rgba(255,107,107,0.10)" : "rgba(15,18,38,0.04)", color: red ? "#B91C1C" : "#64748B" }}>{icon}</div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3">{label}</div>
        <div className={`text-sm leading-snug ${red ? "text-ink" : "text-ink-2"}`}>{value}</div>
      </div>
    </div>
  );
}

// =====================================================================
//  STAGE BLOCK
// =====================================================================
function StageBlock({ stage, openDrawer, slotToUrl }) {
  // Earn stage has a branch: nodes[0,1,2] then 2 parallel branches [3,4] off of node[2]
  const isEarn = stage.id === "earn";

  return (
    <section className="px-6 md:px-10" data-testid={`flow-stage-${stage.id}`}>
      <div className="max-w-[1280px] mx-auto">
        {/* Stage header */}
        <div className="glass-strong p-6 mb-6 fade-up" style={{ borderRadius: 22 }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-display text-lg font-bold" style={{ background: `linear-gradient(135deg, ${stage.accent}, ${stage.accent}AA)`, boxShadow: `0 10px 24px ${stage.accent}33` }}>
              {stage.label.replace(/^Stage\s*/i, "")}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3">{stage.label}</div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{stage.title}</h2>
              <p className="text-sm text-ink-2 mt-0.5">{stage.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Node row */}
        {!isEarn ? (
          <div className="flex flex-col md:flex-row md:flex-wrap md:items-stretch gap-4 md:gap-0">
            {stage.nodes.map((node, i) => (
              <React.Fragment key={node.id}>
                <NodeCard node={node} accent={stage.accent} accentSoft={stage.accentSoft} onClick={openDrawer} index={i} hasVideo={!!(node.slot && slotToUrl?.[node.slot])} />
                {i < stage.nodes.length - 1 && (
                  <>
                    <FlowArrow accent={stage.accent} />
                    <MobileArrow accent={stage.accent} />
                  </>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <EarnBranchLayout stage={stage} openDrawer={openDrawer} slotToUrl={slotToUrl} />
        )}
      </div>
    </section>
  );
}

function EarnBranchLayout({ stage, openDrawer, slotToUrl }) {
  // [0] mining, [1] claim-ca, [2] decision-stake, [3] stake-flex, [4] stake-fixed
  const [mining, claim, decision, flex, fixed] = stage.nodes;
  const hv = (n) => !!(n?.slot && slotToUrl?.[n.slot]);
  return (
    <div className="flex flex-col items-stretch gap-3">
      {/* Linear part */}
      <div className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-0">
        <NodeCard node={mining}   accent={stage.accent} accentSoft={stage.accentSoft} onClick={openDrawer} index={0} hasVideo={hv(mining)} />
        <FlowArrow accent={stage.accent} /><MobileArrow accent={stage.accent} />
        <NodeCard node={claim}    accent={stage.accent} accentSoft={stage.accentSoft} onClick={openDrawer} index={1} hasVideo={hv(claim)} />
        <FlowArrow accent={stage.accent} /><MobileArrow accent={stage.accent} />
        <div className="flex justify-center items-center">
          <DiamondNode node={decision} accent={stage.accent} accentSoft={stage.accentSoft} onClick={openDrawer} index={2} />
        </div>
      </div>
      {/* Branches */}
      <div className="flex justify-center mt-4">
        <div className="w-full max-w-2xl">
          <div className="flex justify-center text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-3">→ Pick one →</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <NodeCard node={flex}  accent={stage.accent} accentSoft={stage.accentSoft} onClick={openDrawer} index={3} hasVideo={hv(flex)} />
            <NodeCard node={fixed} accent={stage.accent} accentSoft={stage.accentSoft} onClick={openDrawer} index={4} hasVideo={hv(fixed)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
//  STAGE→STAGE FLOW CONNECTOR  (vertical, with animated asset chip)
// =====================================================================
function StageConnector({ accent, label, asset }) {
  return (
    <div className="flex flex-col items-center py-8 px-6" data-testid={`flow-connector-${asset.toLowerCase()}`}>
      <div className="relative flex flex-col items-center">
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-current to-current" style={{ color: accent }} />
        <div className="px-3 py-1.5 rounded-full glass-sm flex items-center gap-2" style={{ background: "rgba(255,255,255,0.85)" }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent, boxShadow: `0 0 0 4px ${accent}22` }} />
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-2">{asset}</span>
          <span className="text-xs text-ink">{label}</span>
        </div>
        <ArrowDown size={18} className="mt-1" style={{ color: accent }} />
      </div>
    </div>
  );
}

// =====================================================================
//  MAIN PAGE
// =====================================================================
export default function FlowDiagram() {
  const [active, setActive] = useState(null);
  const [slotToUrl, setSlotToUrl] = useState({});
  const [slotContent, setSlotContent] = useState({});
  const [invite, setInvite] = useState(null);
  const activeStage = active ? STAGES.find((s) => s.nodes.find((n) => n.id === active.id)) : null;
  const activeAccent = activeStage?.accent || "#4060FF";

  // Pull the slot -> stream URL map AND the full per-step content (copy items,
  // notes, body) from the same manifest OnboardingV2 uses, so the flow modal
  // shows the exact same information trainees see in onboarding. Public endpoint.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/onboarding-v2/content`).then((r) => {
      if (!alive) return;
      const map = {}, content = {};
      const walk = (arr, prefix) => (arr || []).forEach((step, i) => {
        const slot = step.id === "intro-video" ? "intro" : `${prefix}${i + 1}`;
        if (step.video_url) map[slot] = step.video_url;
        content[slot] = step;
      });
      walk(r.data?.pre_steps, "pre");
      walk(r.data?.section_a, "a");
      walk(r.data?.section_b, "b");
      setSlotToUrl(map);
      setSlotContent(content);
    }).catch(() => { setSlotToUrl({}); setSlotContent({}); });
    return () => { alive = false; };
  }, []);

  // The caller's personal CaryPact invite link (surfaced on the link steps).
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/me/carypact-invite`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("cp_token") || ""}` },
    }).then((r) => { if (alive) setInvite(r.data || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen pb-24">
      <Header variant="public" />

      {/* HERO */}
      <section className="px-6 md:px-10 pt-12 pb-10">
        <div className="max-w-[1280px] mx-auto">
          <div className="pill mb-5" data-testid="flow-pill"><span className="dot" /> Visual flow · click any node for details</div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
            The whole BotChainCP + CaryPact flow, on <span className="gradient-text">one screen</span>.
          </h1>
          <p className="text-lg text-ink-2 max-w-2xl mt-4">
            From "I'm curious" all the way to "USDT in my bank account." Click any node to open the full explanation — what happens, what it costs, how long it takes, what can go wrong.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6 text-xs font-mono uppercase tracking-wider text-ink-3" data-testid="flow-legend">
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#4060FF" }} /> Wallet setup (A)</div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#6B4FFF" }} /> First transactions (B)</div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#00B344" }} /> Daily earnings</div>
            <div className="flex items-center gap-1.5 ml-auto"><Sparkles size={12} /> Animated dashes = asset flowing this direction</div>
          </div>
        </div>
      </section>

      {/* STAGES + CONNECTORS */}
      <StageBlock stage={STAGES[0]} openDrawer={setActive} slotToUrl={slotToUrl} />
      <StageConnector accent={STAGES[0].accent} label={STAGES[0].flowLabel} asset={STAGES[0].flowAsset} />
      <StageBlock stage={STAGES[1]} openDrawer={setActive} slotToUrl={slotToUrl} />
      <StageConnector accent={STAGES[1].accent} label={STAGES[1].flowLabel} asset={STAGES[1].flowAsset} />
      <StageBlock stage={STAGES[2]} openDrawer={setActive} slotToUrl={slotToUrl} />
      <StageConnector accent={STAGES[2].accent} label={STAGES[2].flowLabel} asset={STAGES[2].flowAsset} />

      {/* OUTCOME / KPI */}
      <section className="px-6 md:px-10 mt-4" data-testid="flow-outcome">
        <div className="max-w-[1280px] mx-auto">
          <div className="glass-strong p-7 md:p-10 fade-up text-center" style={{ borderRadius: 26, background: "linear-gradient(135deg, rgba(64,96,255,0.07), rgba(0,179,68,0.07), rgba(255,140,180,0.05))" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white mx-auto mb-4" style={{ background: "linear-gradient(135deg, #4060FF, #00B344)", boxShadow: "0 14px 30px rgba(64,96,255,0.30)" }}>
              <Target size={26} />
            </div>
            <div className="text-xs uppercase tracking-wider font-mono text-ink-3 mb-1">Principal Recovery KPI</div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight">USDT recovered ÷ USDT invested</h2>
            <p className="text-base text-ink-2 mt-3 max-w-xl mx-auto">
              This is the only number BotChainCP tells you to optimize for. Not daily yield, not rank, not team volume — recovered principal. Every withdrawal logged in your Dashboard bumps this number toward 100%.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <Link to="/dashboard" className="btn-primary" style={{ padding: "12px 22px", fontSize: 14 }} data-testid="flow-outcome-dashboard">
                <Zap size={15} /> Open my Dashboard
              </Link>
              <Link to="/wallets" className="btn-secondary" style={{ padding: "12px 18px", fontSize: 13 }} data-testid="flow-outcome-wallets">
                See my wallets <ChevronRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NodeDrawer node={active} accent={activeAccent} stageName={activeStage?.title} slotToUrl={slotToUrl} content={active ? slotContent[active.slot] : null} invite={invite} onClose={() => setActive(null)} />
      <HelpFab />
    </div>
  );
}
